/**
 * LookFeelPanel — project-scoped style/mood reference uploader for KEYFRAME+.
 *
 * Operator drops images (lighting / color / mood inspiration). Each ref carries
 * two axis arrays:
 *   • use_for      → axes the K+ composer should BORROW
 *   • do_not_copy  → axes it must NOT borrow (identity, brand, wardrobe, etc.)
 *
 * Refs persist on `project.look_feel_refs` and are auto-merged into every K+
 * run/batch request by the backend — operators upload once, fire K+ many
 * times without re-uploading. Cap is enforced server-side (8 refs).
 *
 * Usage:
 *   <LookFeelPanel projectId={projectId} onChange={count => setLfCount(count)} />
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, UploadCloud, Trash2, X, Sparkles, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from '../utils/toast';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USE_FOR_AXES = ['lighting', 'color', 'lensing', 'framing', 'texture', 'mood', 'design', 'skin'];
const DO_NOT_COPY_AXES = ['actor', 'brand', 'logo', 'wardrobe', 'product', 'exact_composition', 'scene_blocking'];

export default function LookFeelPanel({ projectId, onChange, defaultExpanded = false }) {
  const [refs, setRefs] = useState([]);
  const [cap, setCap] = useState(8);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [dragOver, setDragOver] = useState(false);
  const [preview, setPreview] = useState(null);
  const fileInputRef = useRef(null);

  const fetchRefs = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/projects/${projectId}/look-feel-refs`);
      setRefs(data?.items || []);
      setCap(data?.cap || 8);
      if (onChange) onChange((data?.items || []).length);
    } catch (e) {
      console.error('Look-feel fetch failed', e);
    } finally {
      setLoading(false);
    }
  }, [projectId, onChange]);

  useEffect(() => { fetchRefs(); }, [fetchRefs]);

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (refs.length + files.length > cap) {
      toast.error(`Look-feel cap is ${cap}. Remove one before adding more.`);
      return;
    }
    setUploading(true);
    try {
      // Sequential to keep order deterministic + clean toast feedback.
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append('file', f);
        fd.append('use_for', 'lighting,color,mood');                                   // sensible default
        fd.append('do_not_copy', DO_NOT_COPY_AXES.join(','));                          // hard defaults: protect identity/brand
        const { data } = await axios.post(`${API}/projects/${projectId}/look-feel-refs`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        if (data?.ref) {
          setRefs((cur) => {
            const next = [...cur, data.ref];
            if (onChange) onChange(next.length);
            return next;
          });
        }
      }
      toast.success(`Uploaded ${files.length} reference${files.length > 1 ? 's' : ''}`);
    } catch (e) {
      toast.error('Upload failed: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const toggleAxis = async (ref, kind, axis) => {
    const list = kind === 'use_for' ? [...(ref.use_for || [])] : [...(ref.do_not_copy || [])];
    const idx = list.indexOf(axis);
    if (idx >= 0) list.splice(idx, 1); else list.push(axis);
    // Optimistic update
    setRefs((cur) => cur.map((r) => r.id === ref.id ? { ...r, [kind]: list } : r));
    try {
      await axios.patch(`${API}/projects/${projectId}/look-feel-refs/${ref.id}`, { [kind]: list });
    } catch (e) {
      toast.error('Update failed — reverting');
      fetchRefs();
    }
  };

  const updateNote = async (ref, note) => {
    setRefs((cur) => cur.map((r) => r.id === ref.id ? { ...r, note } : r));
    try {
      await axios.patch(`${API}/projects/${projectId}/look-feel-refs/${ref.id}`, { note });
    } catch (e) {
      toast.error('Note save failed');
    }
  };

  const removeRef = async (refId) => {
    if (!window.confirm('Remove this reference?')) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/look-feel-refs/${refId}`);
      setRefs((cur) => {
        const next = cur.filter((r) => r.id !== refId);
        if (onChange) onChange(next.length);
        return next;
      });
      toast.success('Reference removed');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  return (
    <div
      className="rounded-lg border border-white/10 bg-white/[0.02] backdrop-blur-sm"
      data-testid="look-feel-panel"
    >
      {/* Header — always visible, click to expand */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/[0.03] transition rounded-lg"
        data-testid="look-feel-toggle"
      >
        <div className="flex items-center gap-2">
          {expanded ? <ChevronDown className="w-4 h-4 text-white/60" /> : <ChevronRight className="w-4 h-4 text-white/60" />}
          <Sparkles className="w-4 h-4 text-[#0EA5E9]" />
          <span className="text-sm font-medium text-white/90">Look &amp; Feel References</span>
          {refs.length > 0 && (
            <span
              className="ml-1 px-1.5 py-0.5 text-[10px] font-semibold rounded-md bg-[#0EA5E9]/15 text-[#0EA5E9]"
              data-testid="look-feel-count"
            >
              {refs.length} {refs.length === 1 ? 'ref' : 'refs'}
            </span>
          )}
        </div>
        <span className="text-[10px] text-white/40">
          {refs.length === 0 ? 'optional · style only' : `${refs.length}/${cap}`}
        </span>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-1 space-y-3">
          <p className="text-[11px] text-white/50 leading-relaxed">
            Drop inspiration images for <span className="text-[#0EA5E9]">lighting, color, mood, texture</span>.
            K+ will borrow style only — identity, wardrobe, brand, and composition stay locked to Asset Bible + Stage 2.
            Refs are saved on the project and apply to every K+ generation until you remove them.
          </p>

          {/* Drop zone */}
          {refs.length < cap && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFiles(e.dataTransfer.files); }}
              onClick={() => fileInputRef.current?.click()}
              className={`flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed py-6 cursor-pointer transition ${
                dragOver ? 'border-[#0EA5E9] bg-[#0EA5E9]/5' : 'border-white/15 hover:border-white/30 hover:bg-white/[0.03]'
              }`}
              data-testid="look-feel-dropzone"
            >
              {uploading ? (
                <Loader2 className="w-5 h-5 text-[#0EA5E9] animate-spin" />
              ) : (
                <UploadCloud className="w-5 h-5 text-white/40" />
              )}
              <span className="text-[11px] text-white/60">
                {uploading ? 'Uploading…' : 'Drop images or click to upload'}
              </span>
              <span className="text-[9px] text-white/30">
                PNG · JPG · WEBP · up to 8 MB · max {cap} refs
              </span>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/heic"
                multiple
                hidden
                onChange={(e) => handleFiles(e.target.files)}
                data-testid="look-feel-file-input"
              />
            </div>
          )}

          {/* Cards */}
          {loading ? (
            <div className="text-[11px] text-white/40 flex items-center gap-2">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading refs…
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="look-feel-list">
              {refs.map((ref) => (
                <RefCard
                  key={ref.id}
                  ref_={ref}
                  onToggle={(kind, axis) => toggleAxis(ref, kind, axis)}
                  onNote={(n) => updateNote(ref, n)}
                  onRemove={() => removeRef(ref.id)}
                  onPreview={() => setPreview(ref)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview lightbox */}
      {preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
          onClick={() => setPreview(null)}
          data-testid="look-feel-preview-lightbox"
        >
          <div className="relative max-w-[80vw] max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setPreview(null)}
              className="absolute -top-3 -right-3 p-1.5 rounded-full bg-white text-black hover:bg-white/90"
            >
              <X className="w-4 h-4" />
            </button>
            <img src={preview.image_url} alt="Look-feel ref" className="max-w-[80vw] max-h-[80vh] rounded-lg" />
            {preview.note && (
              <div className="absolute bottom-2 left-2 right-2 px-3 py-1.5 rounded-md bg-black/60 backdrop-blur-sm text-white text-[11px]">
                {preview.note}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function RefCard({ ref_, onToggle, onNote, onRemove, onPreview }) {
  const [noteDraft, setNoteDraft] = useState(ref_.note || '');
  useEffect(() => { setNoteDraft(ref_.note || ''); }, [ref_.note]);

  return (
    <div
      className="flex gap-3 p-2.5 rounded-lg bg-white/[0.03] border border-white/5"
      data-testid={`look-feel-card-${ref_.id}`}
    >
      <button
        type="button"
        onClick={onPreview}
        className="shrink-0 w-20 h-20 rounded-md overflow-hidden bg-black/30 hover:ring-2 hover:ring-[#0EA5E9]/50 transition"
        title="Click to preview"
      >
        <img src={ref_.image_url} alt="ref" className="w-full h-full object-cover" />
      </button>
      <div className="flex-1 min-w-0 space-y-1.5">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[10px] text-white/40">
            Slot {(ref_.id || '').slice(0, 6)}
          </div>
          <button
            type="button"
            onClick={onRemove}
            className="p-0.5 text-white/40 hover:text-rose-400 transition"
            title="Remove this reference"
            data-testid={`look-feel-remove-${ref_.id}`}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>

        {/* USE FOR — green */}
        <div>
          <div className="text-[9px] uppercase tracking-wide text-emerald-400/80 mb-0.5">Copy</div>
          <div className="flex flex-wrap gap-1">
            {USE_FOR_AXES.map((a) => {
              const on = (ref_.use_for || []).includes(a);
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => onToggle('use_for', a)}
                  className={`px-1.5 py-0.5 rounded text-[9px] transition ${
                    on
                      ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
                      : 'bg-white/[0.04] text-white/40 border border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`look-feel-${ref_.id}-use_for-${a}`}
                >
                  {a}
                </button>
              );
            })}
          </div>
        </div>

        {/* DO NOT COPY — red */}
        <div>
          <div className="text-[9px] uppercase tracking-wide text-rose-400/80 mb-0.5">Don't copy</div>
          <div className="flex flex-wrap gap-1">
            {DO_NOT_COPY_AXES.map((a) => {
              const on = (ref_.do_not_copy || []).includes(a);
              return (
                <button
                  key={a}
                  type="button"
                  onClick={() => onToggle('do_not_copy', a)}
                  className={`px-1.5 py-0.5 rounded text-[9px] transition ${
                    on
                      ? 'bg-rose-500/20 text-rose-300 border border-rose-500/40'
                      : 'bg-white/[0.04] text-white/40 border border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`look-feel-${ref_.id}-dnc-${a}`}
                >
                  {a.replace('_', ' ')}
                </button>
              );
            })}
          </div>
        </div>

        {/* Note */}
        <input
          type="text"
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          onBlur={() => { if (noteDraft !== (ref_.note || '')) onNote(noteDraft); }}
          placeholder="Optional note (e.g. 'warm shadows, soft falloff')"
          className="w-full px-2 py-1 rounded text-[10px] bg-black/30 border border-white/10 text-white/80 placeholder:text-white/30 focus:border-white/30 focus:outline-none"
          maxLength={500}
          data-testid={`look-feel-note-${ref_.id}`}
        />
      </div>
    </div>
  );
}
